/**
 * useAddNodeWithAnimation — the single funnel every "put a new node on the canvas"
 * path in the editor goes through (palette drag, canvas context menu, file drop from
 * the asset panel, asset "Place on stage", clipboard media paste, Iris node creation).
 *
 * It exists as its own module for one reason beyond tidiness: adding a node with a
 * direct `setNodes` never reaches React Flow's `onNodesChange`, which is the only
 * place the editor marked the flow dirty. Every persistence trigger (the interval,
 * `visibilitychange`, `beforeunload`) gates on `isDirty`, so a node added this way was
 * simply not saved — drop an Image Container, reload, and it is gone. `markDirty` is
 * therefore part of the add operation itself rather than something each of the six
 * call sites has to remember.
 */

import { useCallback } from 'react';
import type { Node } from '@xyflow/react';

export interface AddNodeWithAnimationDeps {
  /** React Flow's `setNodes` (or any equivalent state setter). */
  setNodes: (updater: (nodes: Node[]) => Node[]) => void;
  /** Tracks which nodes are playing their entry animation. */
  setAnimatedNodeIds: (updater: (ids: Set<string>) => Set<string>) => void;
  /** Builds the `onOutput`/`onReceiveInput` handlers for a node id. */
  createNodeHandlers: (nodeId: string) => {
    onOutput: (...args: any[]) => any;
    onReceiveInput: (...args: any[]) => any;
  };
  /** Highest z-index currently in use, so the new node lands on top. */
  getHighestZIndex: () => number;
  /** Tells auto-save the graph changed. Non-optional on purpose — see above. */
  markDirty: () => void;
  /** Entry-animation duration in ms. */
  animationDurationMs?: number;
  /**
   * Marks an undo step before the node is added. Every caller of this hook is
   * a user action (palette, menu, drop, paste, asset placement, Iris); several
   * adds in one tick (multi-place) collapse into one step.
   */
  recordHistory?: (label: string) => void;
}

export type AddNodeWithAnimation = (newNode: Node) => void;

export function useAddNodeWithAnimation(deps: AddNodeWithAnimationDeps): AddNodeWithAnimation {
  const {
    setNodes,
    setAnimatedNodeIds,
    createNodeHandlers,
    getHighestZIndex,
    markDirty,
    animationDurationMs = 400,
    recordHistory,
  } = deps;

  return useCallback((newNode: Node) => {
    recordHistory?.('Add node');
    // Always assign the highest z-index so new nodes appear on top
    newNode = { ...newNode, zIndex: getHighestZIndex() };

    // Ensure handlers are attached if not already present
    if (!newNode.data?.onOutput || !newNode.data?.onReceiveInput) {
      const handlers = createNodeHandlers(newNode.id);
      newNode = {
        ...newNode,
        data: {
          ...newNode.data,
          onOutput: handlers.onOutput,
          onReceiveInput: handlers.onReceiveInput,
        },
      };
    }

    setNodes(nodes => {
      if (nodes.some(n => n.id === newNode.id)) return nodes;
      return [...nodes, newNode];
    });

    // A direct setNodes never reaches onNodesChange, so without this the auto-save
    // never learns about the node and it is lost on reload. Marked unconditionally:
    // the updater above runs later (React defers it), so we cannot know here whether
    // the id was a duplicate — and marking a no-op dirty only costs one extra save.
    markDirty();

    // Add to animated set for CSS animation
    setAnimatedNodeIds(prev => new Set(prev).add(newNode.id));

    // Remove from animated set after animation completes
    setTimeout(() => {
      setAnimatedNodeIds(prev => {
        const next = new Set(prev);
        next.delete(newNode.id);
        return next;
      });
    }, animationDurationMs);
  }, [setNodes, setAnimatedNodeIds, createNodeHandlers, getHighestZIndex, markDirty, animationDurationMs, recordHistory]);
}

export default useAddNodeWithAnimation;
